import {Component, Input, OnChanges, OnInit, Optional} from '@angular/core';
import {Translatable, TranslationService} from '@ngmedax/translation';
import {Questionnaire} from '@ngmedax/common-questionnaire-types';
import {ValueService} from '@ngmedax/value';
import {AbstractControl, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {TRANSLATION_EDITOR_SCOPE} from '../../../constants';
import {KEYS} from '../../../translation-keys';
import {variableNameMessage} from '../../shared/variable-name-hint';
import {QuestionnaireVariablesService} from '../../services/questionnaire-variables.service';
import {FormulaValidationService, FormulaValidationError} from '../../services/formula-validation.service';
import {compileFormulas, renameFormulaReferences} from '../../services/formula-compiler';
import {DependencyCatalogService} from '../dependency-editor/dependency-catalog.service';
import {TemplateVariable, buildTemplateVariables} from '../../froala/template-variables';
import {ToastService} from '../../../../../toast';


// hack to inject decorator declarations. must occur before class declaration!
export interface FormulaVariablesComponent extends Translatable {}

@Component({
  selector: 'app-qa-formula-variables',
  templateUrl: './formula-variables.component.html',
  styleUrls: ['./formula-variables.component.css', '../../shared/settings-controls.css']
})
@Translatable({scope: TRANSLATION_EDITOR_SCOPE, keys: KEYS})
export class FormulaVariablesComponent implements OnInit, OnChanges {
  @Input() questionnaire: Questionnaire;
  @Input() locale = 'de_DE';
  public formulaSources: TemplateVariable[] = [];
  public form: FormGroup;

  /** Filters the formula list by name or expression */
  public search = '';

  /** Formulas that use a formula, per formula name (shown in the compact rows) */
  public usedBy: {[name: string]: string[]} = {};

  /** Names of the formulas whose editor is shown, all others are compact summary rows */
  public openFormulas = new Set<string>();

  /**
   * Current validation state per formula variable, refreshed by the form validators
   */
  private expressionErrors: {[varName: string]: {message: string, error: FormulaValidationError}} = {};

  /**
   * Cache for the autofix candidates (autofix parses, so don't run it on every change detection)
   */
  private fixableCache: {key: string, names: string[]} = {key: null, names: []};

  private varPath  = ['meta', 'options', 'variables'];
  private varNamePattern = /^[a-zA-Z_][a-zA-Z0-9_]{2,49}$/;
  private errors = {
    'invalidExpression': this._(KEYS.EDITOR.INVALID_EXPRESSION),
    'invalidCircularExpression': this._(KEYS.EDITOR.INVALID_CIRCULAR_EXPRESSION)
  }

  private formulaErrors: {[type: string]: string} = {
    'syntax': this._(KEYS.EDITOR.FORMULA_SYNTAX_ERROR),
    'unknownVariable': this._(KEYS.EDITOR.FORMULA_UNKNOWN_VARIABLE),
    'unknownMathFunction': this._(KEYS.EDITOR.FORMULA_UNKNOWN_MATH_FUNCTION),
    'invalidFunctionCall': this._(KEYS.EDITOR.FORMULA_RUNTIME_CALL),
    'quotes': this._(KEYS.EDITOR.INVALID_EXPRESSION),
    'circular': this._(KEYS.EDITOR.INVALID_CIRCULAR_EXPRESSION),
    'formulaNotInlinable': this._(KEYS.EDITOR.FORMULA_NOT_INLINABLE),
    'formulaBrokenDependency': this._(KEYS.EDITOR.FORMULA_BROKEN_DEPENDENCY)
  }

  private formulaErrorDetails: {[code: string]: string} = {
    'missingClosingParen': this._(KEYS.EDITOR.FORMULA_MISSING_CLOSING_PAREN),
    'extraClosingParen': this._(KEYS.EDITOR.FORMULA_EXTRA_CLOSING_PAREN)
  }

  /**
   * Injects dependencies
   */
  public constructor(
    @Optional() private translationService: TranslationService,
    private questionnaireVariables: QuestionnaireVariablesService,
    private formulaValidation: FormulaValidationService,
    private formBuilder: FormBuilder,
    private toast: ToastService,
    private value: ValueService,
    private catalog: DependencyCatalogService
  ) {
  }

  /**
   * Initializes formula variables and updates form
   */
  public ngOnInit() {
    this.initFormulaVariables();
    this.updateForm();
  }

  /** Refreshes friendly source labels when the questionnaire language changes. */
  public ngOnChanges(): void {
    if (this.form) { this.refreshSources(); }
  }

  /** Refreshes formula sources. Formulas can use other formulas, the editor resolves them for the app. */
  public refreshSources(): void {
    const sources = new Map<string, TemplateVariable>();
    buildTemplateVariables(this.questionnaire, this.questionnaireVariables.getVariableScopes(), this.locale,
      label => this._(label)).forEach(item => sources.set(item.path, item));
    this.catalog.build(this.locale).forEach(item => sources.set(item.path, item));
    this.formulaSources = Array.from(sources.values());
  }

  /** Updates only the edited formula control and preserves the explicit per-formula save action. */
  public onExpressionChange(name: string, expression: string): void {
    const control = this.form.get('expression:' + name);
    control.setValue(expression);
    control.markAsDirty();
  }

  /** Applies the current formula only after both fields pass their validators. */
  public saveFormula(name: string): void {
    if (this.form.get(name).invalid || this.form.get('expression:' + name).invalid) { return; }
    this.onEdit(this.form.get(name).value, name);
  }

  /** True when the editor of the given formula is shown */
  public isFormulaOpen(name: string): boolean {
    return this.openFormulas.has(name);
  }

  /** Shows or hides the editor of the given formula */
  public toggleFormula(name: string): void {
    this.openFormulas.has(name) ? this.openFormulas.delete(name) : this.openFormulas.add(name);
  }

  /** True when every formula is open */
  public get allFormulasOpen(): boolean {
    const names = this.getFormulaVariableNames(false);
    return !!names.length && names.every(name => this.openFormulas.has(name));
  }

  /** Opens all formulas, or closes all when all are open */
  public toggleAllFormulas(): void {
    this.openFormulas = this.allFormulasOpen ? new Set() : new Set(this.getFormulaVariableNames(false));
  }

  /**
   * Adds new formula variable
   */
  public onAdd() {
    // the new formula has to be visible
    this.search = '';
    const varPrefix = 'formula';
    const formulaVariables = this.questionnaireVariables.getVariablesForScope('formula');

    for (let i = 1; i < 1000; i++) {
      const varName = `${varPrefix}${i}`;
      if (!formulaVariables[varName]) {
        formulaVariables[varName] = {expression: ''};
        // a new formula opens right away
        this.openFormulas.add(varName);
        break;
      }
    }

    this.updateForm();
  }

  /**
   * Event handler for when a formula var is changed
   *
   * @param {string} varName
   * @param {string} previousVarName
   */
  public onEdit(varName: string, previousVarName: string) {
    if (this.form.get(previousVarName).invalid || this.form.get('expression:' + previousVarName).invalid) { return; }
    const variables = this.questionnaireVariables.getVariablesForScope('formula');
    if (varName !== previousVarName && varName in variables) {
      this.toast.error(this._(KEYS.EDITOR.FORMULA_DUPLICATE_NAME));
      return;
    }
    const expressionValue = this.form.get('expression:' + previousVarName)?.value;
    this.renameVariable('formula', varName, expressionValue, previousVarName);
    varName !== previousVarName && this.renameReferences(previousVarName, varName);
    // a renamed formula stays open
    if (varName !== previousVarName && this.openFormulas.delete(previousVarName)) {
      this.openFormulas.add(varName);
    }
    this.form.get(previousVarName).markAsPristine();
    this.form.get('expression:' + previousVarName).markAsPristine();
    this.updateForm();
  }

  /**
   * Event handler for when formula var is reset to previous value
   *
   * @param {string} previousVarName
   */
  public onUndo(previousVarName: string) {
    const variables = this.questionnaireVariables.getVariablesForScope('formula');
    const previousExpression = this.value.get(variables, [previousVarName, 'expression']) || '';

    this.form.get(previousVarName).reset(previousVarName);
    this.form.get('expression:' + previousVarName).reset(previousExpression);
  }

  /**
   * Event handler for when formula var is removed
   *
   * @param {string} varName
   */
  public onRemove(varName: string) {
    const formulaVariables = this.questionnaireVariables.getVariablesForScope('formula');
    formulaVariables[varName] && delete(formulaVariables[varName]);
    delete(this.expressionErrors[varName]);
    this.openFormulas.delete(varName);
    this.updateForm();
  }

  /**
   * Fixes given formula when it is mechanically fixable (unbalanced parentheses,
   * "else (x)"). Writes the fixed expression directly into the questionnaire
   * model, so the per formula save step is not needed. Persisting still happens
   * with the regular questionnaire save
   *
   * @param {string} varName
   */
  public onAutoFix(varName: string) {
    const variables = this.questionnaireVariables.getVariablesForScope('formula');
    const control = this.form.get('expression:' + varName);

    if (!control || !variables[varName]) {
      return;
    }

    const fixed = this.formulaValidation.autofix(control.value + '', this.questionnaire);

    if (fixed === null) {
      return;
    }

    variables[varName].expression = fixed.trim();

    control.setValue(fixed);
    control.markAsPristine();

    this.toast.success(`${this._(KEYS.EDITOR.FORMULA_AUTOFIX_DONE)}: ${varName}`);
  }

  /**
   * Returns true when the formula of given variable is broken and the autofix
   * can repair it. Used to enable the per formula fix button
   *
   * @param {string} varName
   * @returns {boolean}
   */
  public isFormulaFixable(varName: string): boolean {
    return this.getFixableFormulaNames().indexOf(varName) !== -1;
  }

  /**
   * Returns names of broken formulas that the autofix can repair. Cached by the
   * current expression values, as this runs from the template
   *
   * @returns {string[]}
   */
  public getFixableFormulaNames(): string[] {
    if (!this.form) {
      return [];
    }

    const varNames = this.getFormulaVariableNames(false);
    const key = varNames.map(name => {
      const control = this.form.get('expression:' + name);
      return `${name}|${control ? control.value : ''}`;
    }).join('##');

    if (this.fixableCache.key === key) {
      return this.fixableCache.names;
    }

    const names = varNames.filter(name => {
      const control = this.form.get('expression:' + name);

      if (!control || !control.errors) {
        return false;
      }

      return this.formulaValidation.autofix(control.value + '', this.questionnaire) !== null;
    });

    this.fixableCache = {key, names};
    return names;
  }

  /**
   * Returns current error message for the error box below given formula
   *
   * @param {string} varName
   * @returns {string}
   */
  public getFormulaErrorText(varName: string): string {
    const info = this.expressionErrors[varName];
    return info ? info.message : '';
  }

  /**
   * Returns true when the formula of given variable is currently invalid
   *
   * @param {string} varName
   * @returns {boolean}
   */
  public hasFormulaError(varName: string): boolean {
    return !!this.expressionErrors[varName];
  }

  /** Returns the offending source range for both supported and source presentations. */
  public formulaErrorRange(varName: string): {start: number; end: number} {
    return this.expressionErrors[varName]?.error.range || null;
  }

  /**
   * Handles keyboard shortcuts on formula variable name input fields.
   * Enter saves the variable, Ctrl+S saves and stops event propagation.
   *
   * @param {KeyboardEvent} event
   * @param {HTMLInputElement} formulaInputEl
   * @param {string} formulaVarName
   */
  public onNameKeyDown(event: KeyboardEvent, formulaInputEl: HTMLInputElement, formulaVarName: string) {
    const isCtrlS = (event.ctrlKey || event.metaKey) && (event.key === 's' || event.key === 'S');
    const isEnter = event.key === 'Enter';

    if (!isCtrlS && !isEnter) {
      return;
    }

    event.preventDefault();

    event.stopPropagation();

    const hasErrors = this.form.get(formulaVarName).errors
      || this.form.get('expression:' + formulaVarName).errors;

    if (hasErrors || !this.form.dirty) {
      return;
    }

    this.onEdit(formulaInputEl.value, formulaVarName);
  }

  /**
   * Explains in plain words why the changed name is not accepted, shown below the name field
   *
   * @param {string} varName
   */
  public nameError(varName: string): string {
    return variableNameMessage(this.form.get(varName), key => this._(key));
  }


  /**
   * Builds a user readable error message for given formula validation error
   *
   * @param {FormulaValidationError} error
   * @returns {string}
   */
  private getFormulaErrorMessage(error: FormulaValidationError): string {
    const message = this.formulaErrors[error.type] || this.errors.invalidExpression;

    if (error.detailCode && this.formulaErrorDetails[error.detailCode]) {
      return `${message}: ${this.formulaErrorDetails[error.detailCode]}`;
    }

    const withoutDetail = error.type === 'quotes';
    return (!withoutDetail && error.detail) ? `${message}: ${error.detail}` : message;
  }

  /**
   * Returns array of formula variables by questionnaire or by form
   *
   * @param {boolean} byForm
   * @returns string[]
   */
  /**
   * Whether a formula matches the search by its name or expression
   */
  public matchesSearch(varName: string): boolean {
    const search = this.search.trim().toLowerCase();
    if (!search) {
      return true;
    }
    const control = this.form && this.form.get('expression:' + varName);
    const expression = control && typeof control.value === 'string' ? control.value : '';
    return varName.toLowerCase().indexOf(search) !== -1 || expression.toLowerCase().indexOf(search) !== -1;
  }

  /**
   * Whether at least one formula matches the search
   */
  public hasSearchResults(): boolean {
    return this.getFormulaVariableNames(false).some(name => this.matchesSearch(name));
  }

  public getFormulaVariableNames(byForm = false): string[] {
    const formControls = this.value.get(this.form, ['controls']) || {};
    return Object.keys(byForm ? formControls : this.questionnaireVariables.getVariablesForScope('formula'));
  }

  /**
   * Returns variable data
   *
   * @param {string} varName
   * @returns {{type: string}}
   */
  public getVariableData(varName: string): {format: string, expression: string | number} {
    return <{format: string, expression: string}>
      this.questionnaireVariables.getVariablesForScope('formula')[varName] || {format: 'text', expression: ''};
  }

  /**
   * Updates references to a renamed formula in all other formulas, also in not yet saved drafts
   *
   * @param {string} from
   * @param {string} to
   */
  private renameReferences(from: string, to: string) {
    const variables = this.questionnaireVariables.getVariablesForScope('formula');

    Object.keys(variables).forEach(name => {
      const variable = variables[name];
      variable && typeof variable.expression === 'string' && (variable.expression = renameFormulaReferences(variable.expression, from, to));

      const control = this.form.get('expression:' + name);
      control && control.dirty && typeof control.value === 'string' && control.setValue(renameFormulaReferences(control.value, from, to));
    });
  }

  /**
   * Renames variable
   */
  public renameVariable(scope: string, varName: string, expressionValue: string, previousVarName: string) {
    const variables = this.questionnaireVariables.getVariablesForScope(scope);

    if (!variables[previousVarName]) {
      return;
    }

    variables[varName] = this.value.clone(variables[previousVarName]);
    variables[varName].expression = expressionValue;
    previousVarName !== varName && delete(variables[previousVarName]);
  }

  /**
   * Updates formula variables form by questionnaire formula variables
   */
  private updateForm() {
    const varNames = this.getFormulaVariableNames();
    const previousControls = this.form?.controls || {};
    const dynFormGroup = {};

    this.expressionErrors = {};

    for (const varName of varNames) {
      dynFormGroup[varName] = this.formBuilder.control(varName, [Validators.required, Validators.pattern(this.varNamePattern)]);

      const expressionValue = this.getVariableData(varName).expression || '';
      dynFormGroup['expression:' + varName] = this.formBuilder.control(expressionValue, (control: AbstractControl) => {
        // only formulas that use other formulas can be part of a cycle or use a broken formula
        const usesFormulas = /\bformula\s*(\.|\[)/.test(control.value + '');
        const error = this.formulaValidation.validate(control.value + '', this.questionnaire)
          || (usesFormulas ? this.formulaValidation.validateFormulaUsage(this.questionnaire, {[varName]: control.value + ''})[varName] : null);

        if (!error) {
          delete(this.expressionErrors[varName]);
          return null;
        }

        this.expressionErrors[varName] = {message: this.getFormulaErrorMessage(error), error};

        const errorKey = error.type === 'circular' ? 'invalidCircularExpression' : 'invalidExpression';
        return {[errorKey]: true, formulaError: this.getFormulaErrorMessage(error)};
      });
    }

    Object.keys(dynFormGroup).forEach(key => {
      if (previousControls[key]?.dirty) {
        dynFormGroup[key].setValue(previousControls[key].value);
        dynFormGroup[key].markAsDirty();
      }
    });
    this.form = this.formBuilder.group(dynFormGroup);
    if (Object.values(dynFormGroup).some((control: AbstractControl) => control.dirty)) { this.form.markAsDirty(); }
    this.updateUsage();
    // added, renamed or removed formulas show up in the variable picker right away
    this.refreshSources();
  }

  /**
   * Updates which formulas use which formula, shown in the compact rows
   */
  private updateUsage() {
    const variables = this.questionnaireVariables.getVariablesForScope('formula');
    const sources: {[name: string]: string} = {};
    Object.keys(variables).forEach(name => sources[name] = ((variables[name] || {}).expression || '') + '');

    const results = compileFormulas(sources);
    this.usedBy = {};
    Object.keys(results).forEach(name => this.usedBy[name] = results[name].usedBy);
  }

  /**
   * Initializes questionnaire formula variables in questionnaire
   */
  private initFormulaVariables() {
    let current = this.questionnaire;
    [...this.varPath, 'formula'].forEach((path) => {
      !current[path] && (current[path] = {});
      current = current[path];
    });
  }
}
